import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { acquisitionData } from '../../mock-data/acquisition';
import { operationsSuggestedPromotionData } from '../../mock-data/operations-suggested-promotion';
import { operationsRunningCampaignData } from '../../mock-data/operations-running-campaign';
import { operationsEndedPromotionData } from '../../mock-data/operations-ended-promotion';
import { withRunningCardIdentity, withSuggestedCardIdentity } from '../../utils/card-identity';
import {
  resolveCampaignStatus,
  formatStatusAt,
  useCampaignOverrides,
  useCampaignStatusActions,
} from '../../utils/campaign-status';
import { ROUTE } from '../../routes';
import { useFeatureGate } from '../../hooks/useFeatureGate';
import { useShowInProgress } from '../../hooks/useShowInProgress';
import type { Promotion, RunningCampaignData } from '../../mock-data/types';
import type { OperationsCampaignDetail } from '../operations/api/crm-events';
import SuggestedCampaign from '../suggested-campaign';
import RunningCampaign from '../running-campaign';
import { useOperationsPromotion } from './useOperationsPromotion';

export type OperationsPromotionDetailProps = {
  styleText?: string;
  className?: string;
};

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' && value ? value : undefined;

/**
 * Overlay the real per-campaign detail (`GET /campaigns/{id}`) onto a Running /
 * paused / ended hero: the description becomes the subtitle, the offering name the
 * "Offering" value, and `players_engaged` the first hero stat. Anything the detail
 * response omits keeps the template's sample value (the per-KPI figures below the
 * hero have no API source yet and stay mock). No-op when `detail` is null.
 */
const withDetailHero = (
  data: RunningCampaignData,
  detail: OperationsCampaignDetail | null,
): RunningCampaignData => {
  if (!detail) return data;
  const subtitle = asString(detail.description);
  const offeringValue = asString(detail.offering?.name);
  const playersEngaged = typeof detail.players_engaged === 'number' ? String(detail.players_engaged) : undefined;
  return {
    ...data,
    hero: {
      ...data.hero,
      subtitle: subtitle ?? data.hero.subtitle,
      offering: { ...data.hero.offering, value: offeringValue ?? data.hero.offering.value },
      adSpend: { ...data.hero.adSpend, value: playersEngaged ?? data.hero.adSpend.value },
    },
  };
};

/**
 * Resolve a promotion's linked Acquisition campaign by their shared `linkKey`
 * and add the "also in use for Acquisition" crosslink (pointing at that
 * campaign's detail) to a Running/paused promotion payload. When the promotion
 * has no `linkKey`, or no campaign carries the same key, the payload is returned
 * untouched so no crosslink renders. The crosslink `label` is the (translated)
 * static operations copy, passed in by the caller so it stays reactive. Mock
 * stage only (when wired to an API the detail response will carry the linked
 * campaign id, or this link, directly).
 */
const withAcquisitionCrosslink = (
  data: RunningCampaignData,
  promotion: Promotion,
  crosslinkLabel: string,
): RunningCampaignData => {
  if (!promotion.linkKey) return data;
  const linked = acquisitionData.campaigns.pool.find((c) => c.linkKey === promotion.linkKey);
  if (!linked) return data;
  return {
    ...data,
    hero: { ...data.hero, crosslink: { label: crosslinkLabel, to: ROUTE.campaignDetail(linked.id) } },
  };
};

/**
 * Route dispatcher for `account/operations/promotion/:id` — fetches the promotion
 * by id from the CRM Events `/campaigns/{id}` endpoint, resolves its effective
 * status (its base status reconciled with any runtime pause/resume and the expiry
 * rule, see `utils/campaign-status`) and renders the Suggested or Running campaign
 * template with the operations copy. The real title / artwork / period / offering /
 * description are fed into the template; the per-KPI figures below the hero have no
 * API source yet and stay mock (see `withDetailHero`). Mirrors the acquisition
 * `campaign-detail`.
 */
const OperationsPromotionDetail = ({
  styleText = '',
  className = '',
}: OperationsPromotionDetailProps): React.ReactElement | null => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const overrides = useCampaignOverrides();
  const { activate, pause, resume } = useCampaignStatusActions();
  const { isLocked } = useFeatureGate();
  const { promotion, detail, loading } = useOperationsPromotion(id);

  // The CRM Events API exposes no per-promotion performance figures yet, so the detail's
  // "Campaign Performance" grid is still mock — gate it behind an "Unlocking soon" veil
  // rather than showing invented numbers. Reactive: reveals live via `show_in_progress`.
  const performanceLocked = isLocked('promotion-performance');
  // The Acquisition campaign pages open only under the plain "777" reveal, so the crosslink into one shows only then.
  const acquisitionCampaignsShown = useShowInProgress();

  // Nothing to render until the campaign resolves; a null after loading means the
  // id is unknown (e.g. a stale link) — the list is where the owner recovers.
  if (loading || !promotion) return null;

  const status = resolveCampaignStatus(
    promotion.status,
    promotion.expiresAt,
    overrides[promotion.id]?.status,
    Date.now(),
  );

  if (status === 'suggestion') {
    const data = withSuggestedCardIdentity(operationsSuggestedPromotionData, promotion);
    // Activate → flip to running, then drop the owner onto the Operations
    // Running tab with this promotion flagged (via location state) for the
    // "just activated" highlight.
    const onActivate = (): void => {
      activate(promotion.id);
      navigate(`${ROUTE.operations()}?tab=running`, { state: { activatedId: promotion.id } });
    };
    return <SuggestedCampaign data={data} onActivate={onActivate} styleText={styleText} className={className} />;
  }

  // Ended promotions open the same template in its historical (ended) mode, with
  // the clicked promotion's own end date (the expiry deadline if it ended by
  // passing it).
  if (status === 'ended') {
    const endedOn =
      promotion.status === 'ended'
        ? (promotion.statusAt ?? operationsEndedPromotionData.hero.endedOn)
        : promotion.expiresAt != null
          ? formatStatusAt(promotion.expiresAt)
          : (promotion.statusAt ?? operationsEndedPromotionData.hero.endedOn);
    const data = withDetailHero(
      withRunningCardIdentity(
        { ...operationsEndedPromotionData, hero: { ...operationsEndedPromotionData.hero, endedOn } },
        promotion,
      ),
      detail,
    );
    return (
      <RunningCampaign
        data={data}
        status="ended"
        kind="promotion"
        performanceLocked={performanceLocked}
        styleText={styleText}
        className={className}
      />
    );
  }

  // Running or paused → the live Running template (a paused promotion is still a
  // running-promotion detail; it just shows the Resume button + re-titled header).
  // The "also in use for Acquisition" crosslink is added when this promotion is
  // linked to an acquisition campaign (shared `linkKey`).
  const identified = withRunningCardIdentity(operationsRunningCampaignData, promotion);
  const base = withDetailHero(
    acquisitionCampaignsShown
      ? withAcquisitionCrosslink(identified, promotion, t('Promotion also in use for Acquisition'))
      : identified,
    detail,
  );
  const data = status === 'paused' ? { ...base, header: { title: t('Paused Promotion Details') } } : base;
  return (
    <RunningCampaign
      data={data}
      status={status}
      kind="promotion"
      performanceLocked={performanceLocked}
      onPause={() => pause(promotion.id)}
      onResume={() => resume(promotion.id, { expiresAt: promotion.expiresAt })}
      styleText={styleText}
      className={className}
    />
  );
};

export default OperationsPromotionDetail;
